import { useMemo } from "react";
import { Flex, Text } from "@radix-ui/themes";
import { EmptyCircleIcon, CheckCircleIcon } from "@/assets/icons";
import { cn } from "@/lib/utils";

type PasswordRequirementsChecklistProps = {
  password: string;
};

type RequirementItemProps = {
  isMet: boolean;
  requirement: string;
};

const requirementsConfig = [
  {
    requirement: "1 uppercase letter",
    check: (p: string) => /[A-Z]/.test(p),
  },
  {
    requirement: "1 lowercase letter",
    check: (p: string) => /[a-z]/.test(p),
  },
  {
    requirement: "1 special character",
    check: (p: string) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(p),
  },
  { requirement: "1 number", check: (p: string) => /[0-9]/.test(p) },
  {
    requirement: "8 characters",
    check: (p: string) => p.length >= 8,
  },
];

const RequirementItem = ({ isMet, requirement }: RequirementItemProps) => {
  const IconComponent = isMet ? CheckCircleIcon : EmptyCircleIcon;

  return (
    <Flex align="center" gap="2">
      <Flex className="transition-colors duration-300 flex-shrink-0">
        <IconComponent
          className={cn("w-3.5 h-3.5 sm:w-4 sm:h-4", {
            "text-blue-600": isMet,
            "text-black": !isMet,
          })}
        />
      </Flex>
      <Text as="span" size="2" className="leading-[1.4] text-blue-8">
        {requirement}
      </Text>
    </Flex>
  );
};

export function PasswordRequirementsChecklist({
  password,
}: PasswordRequirementsChecklistProps) {
  const checkedRequirements = useMemo(() => {
    return requirementsConfig.map((req) => ({
      requirement: req.requirement,
      isMet: req.check(password),
    }));
  }, [password]);

  return (
    <div className="flex flex-col gap-1.5 sm:grid sm:grid-cols-2 sm:gap-x-6 sm:gap-y-2 pt-2">
      {checkedRequirements.map((item) => (
        <RequirementItem
          key={item.requirement}
          isMet={item.isMet}
          requirement={item.requirement}
        />
      ))}
    </div>
  );
}

export default PasswordRequirementsChecklist;
